import { postAdminRefresh } from "@/lib/api/admin-auth-api";
import { ApiError } from "@/lib/api/api-error";
import {
  getPersistedRefreshToken,
  setPersistedAccessToken,
  setPersistedRefreshToken,
} from "@/lib/auth/admin-session-storage";
import {
  getInMemoryAccessToken,
  setInMemoryAccessToken,
} from "@/lib/auth/admin-token-storage";

function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

function isRefreshUnavailable(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchange the persisted refresh token for a new access token.
 * Coalesces concurrent callers into a single in-flight refresh.
 */
export async function refreshAdminAccessToken(): Promise<string | null> {
  const refreshToken = getPersistedRefreshToken();
  if (!refreshToken) return null;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const refreshed = await postAdminRefresh({
          refresh_token: refreshToken,
        });
        setInMemoryAccessToken(refreshed.access_token);
        setPersistedAccessToken(refreshed.access_token);
        setPersistedRefreshToken(refreshed.refresh_token);
        return refreshed.access_token;
      } catch (err) {
        if (!isUnauthorized(err) && !isRefreshUnavailable(err)) throw err;
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  return refreshInFlight;
}

/** Clears the refresh mutex (tests only). */
export function resetRefreshAdminAccessTokenState(): void {
  refreshInFlight = null;
}

export function hasRefreshableAdminSession(): boolean {
  return Boolean(getPersistedRefreshToken());
}

export function getCurrentAdminAccessToken(): string | null {
  return getInMemoryAccessToken();
}
